/**
 * PATH: apps/hr/src/pages/support/SupportPage.tsx
 * PURPOSE: Support tickets management page
 * 
 * ROLE IN ARCHITECTURE: HR support ticket tracking and management
 * 
 * NOTES FOR FUTURE AI:
 * - Uses real API from workforce-api /support-tickets endpoint
 * - Includes delete functionality with confirmation
 */

import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query'
import {
  Ticket,
  Plus,
  Search,
  Clock,
  AlertCircle,
  CheckCircle2,
  Eye,
  Trash2,
  Loader2,
} from 'lucide-react'

import { cn, formatDate } from '../../lib/utils'
import { hrApi } from '../../lib/setupApi'
import { CreateRequestModal } from '../../components/support/CreateRequestModal'
import { ConfirmModal } from '../../components/ui/Modal'
import type { TicketStatus, TicketPriority } from '../../types'

// =============================================================================
// TYPES
// =============================================================================

interface ApiTicket {
  id: string
  title: string
  description: string
  ticket_type: string
  priority: string
  status: string
  linked_employee_id?: string
  linked_employee?: {
    id: string
    first_name: string
    last_name: string
  }
  linked_document_id?: string
  assignee_id?: string
  assignee?: {
    id: string
    full_name: string
  }
  created_by_id: string
  created_by?: {
    id: string
    full_name: string
  }
  due_date?: string
  created_at: string
  updated_at: string
  comments?: Array<{ id: string }>
}

interface ApiStats {
  total: number
  open: number
  in_progress: number
  pending: number
  resolved: number
  closed: number
  urgent: number
  high_priority: number
  overdue: number
  by_type: Record<string, number>
}

// =============================================================================
// STAT CARD
// =============================================================================

function StatCard({ label, value, icon: Icon, color, isLoading }: {
  label: string
  value: number
  icon: React.ElementType
  color: string
  isLoading?: boolean
}) {
  return (
    <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-4">
      <div className="flex items-center gap-3">
        <div className={cn('w-10 h-10 rounded-xl flex items-center justify-center', color)}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          {isLoading ? (
            <div className="h-8 w-12 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
          ) : (
            <p className="text-2xl font-semibold text-slate-900 dark:text-white">{value}</p>
          )}
          <p className="text-sm text-slate-500">{label}</p>
        </div>
      </div>
    </div>
  )
}

// =============================================================================
// STATUS & PRIORITY STYLES
// =============================================================================

const statusStyles: Record<TicketStatus, string> = {
  open: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  in_progress: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400',
  pending: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400',
  resolved: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  closed: 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
}

const statusLabels: Record<string, string> = {
  open: 'Open',
  in_progress: 'In Progress',
  pending: 'Pending',
  resolved: 'Resolved',
  closed: 'Closed',
}

const priorityStyles: Record<string, string> = {
  low: 'text-slate-500 bg-slate-100 dark:bg-slate-800',
  medium: 'text-blue-600 bg-blue-50 dark:bg-blue-900/20',
  high: 'text-amber-600 bg-amber-50 dark:bg-amber-900/20',
  urgent: 'text-red-600 bg-red-50 dark:bg-red-900/20',
}

// =============================================================================
// MAIN PAGE
// =============================================================================

export default function SupportPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  
  const [statusFilter, setStatusFilter] = useState<'all' | TicketStatus>('all')
  const [search, setSearch] = useState('')
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [ticketToDelete, setTicketToDelete] = useState<ApiTicket | null>(null)

  // Fetch stats
  const { data: statsData, isLoading: statsLoading } = useQuery({
    queryKey: ['support-ticket-stats'],
    queryFn: () => hrApi.getSupportTicketStats(),
    staleTime: 30 * 1000, // 30 seconds
  })

  const stats: ApiStats = statsData?.data || {
    total: 0,
    open: 0,
    in_progress: 0,
    pending: 0,
    resolved: 0,
    closed: 0,
    urgent: 0,
    high_priority: 0,
    overdue: 0,
    by_type: {},
  }

  // Fetch tickets with filters
  const { data: ticketsData, isLoading: ticketsLoading, error: ticketsError } = useQuery({
    queryKey: ['support-tickets', statusFilter, search],
    queryFn: () => hrApi.getSupportTickets({
      status: statusFilter !== 'all' ? statusFilter : undefined,
      search: search.trim() || undefined,
      limit: 100,
    }),
    staleTime: 30 * 1000,
  })

  const tickets: ApiTicket[] = ticketsData?.data?.items || []

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (ticketId: string) => hrApi.deleteSupportTicket(ticketId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['support-tickets'] })
      queryClient.invalidateQueries({ queryKey: ['support-ticket-stats'] })
      setTicketToDelete(null)
    },
  })

  // Filter and sort tickets - client-side filtering for reliability
  const filteredTickets = useMemo(() => {
    const priorityOrder: Record<string, number> = { urgent: 0, high: 1, medium: 2, low: 3 }
    
    let filtered = [...tickets]
    
    // Apply status filter client-side (backup for API)
    if (statusFilter !== 'all') {
      filtered = filtered.filter(t => t.status === statusFilter)
    }
    
    // Apply search filter client-side
    if (search.trim()) {
      const searchLower = search.toLowerCase().trim()
      filtered = filtered.filter(t => 
        t.title.toLowerCase().includes(searchLower) ||
        t.description.toLowerCase().includes(searchLower) ||
        (t.linked_employee && `${t.linked_employee.first_name} ${t.linked_employee.last_name}`.toLowerCase().includes(searchLower))
      )
    }
    
    // Sort by priority then date
    return filtered.sort((a, b) => {
      const priorityDiff = (priorityOrder[a.priority] ?? 2) - (priorityOrder[b.priority] ?? 2)
      if (priorityDiff !== 0) return priorityDiff
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    })
  }, [tickets, statusFilter, search])

  // Handle delete click
  const handleDeleteClick = (e: React.MouseEvent, ticket: ApiTicket) => {
    e.stopPropagation()
    setTicketToDelete(ticket)
  }

  // Confirm delete
  const handleConfirmDelete = () => {
    if (ticketToDelete) {
      deleteMutation.mutate(ticketToDelete.id)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">Support</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
            HR support tickets and requests
          </p>
        </div>
        <button 
          onClick={() => setIsCreateModalOpen(true)}
          className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg text-sm font-medium hover:bg-teal-700"
        >
          <Plus className="w-4 h-4" />
          New Request
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Open"
          value={stats.open}
          icon={AlertCircle}
          color="bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400"
          isLoading={statsLoading}
        />
        <StatCard
          label="In Progress"
          value={stats.in_progress}
          icon={Clock}
          color="bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400"
          isLoading={statsLoading}
        />
        <StatCard
          label="Urgent"
          value={stats.urgent}
          icon={AlertCircle}
          color="bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400"
          isLoading={statsLoading}
        />
        <StatCard
          label="Resolved"
          value={stats.resolved}
          icon={CheckCircle2}
          color="bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400"
          isLoading={statsLoading}
        />
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800 rounded-lg p-1">
          {(['all', 'open', 'in_progress', 'pending', 'resolved'] as const).map((f) => (
            <button
              key={f}
              onClick={() => setStatusFilter(f)}
              className={cn(
                'px-3 py-1.5 text-sm font-medium rounded-md transition-colors capitalize',
                statusFilter === f
                  ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                  : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
              )}
            >
              {f === 'in_progress' ? 'In Progress' : f}
            </button>
          ))}
        </div>
        <div className="relative w-full sm:w-auto">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Search tickets…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full sm:w-64 pl-9 pr-4 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-lg text-sm outline-none focus:ring-2 focus:ring-teal-500"
          />
        </div>
      </div>

      {/* Loading state */}
      {ticketsLoading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-8 h-8 text-teal-600 animate-spin" />
        </div>
      )}

      {/* Error state */}
      {ticketsError && (
        <div className="bg-red-50 dark:bg-red-900/20 rounded-xl border border-red-200 dark:border-red-800 p-4 text-center">
          <AlertCircle className="w-8 h-8 mx-auto text-red-500 mb-2" />
          <p className="text-red-700 dark:text-red-400">Failed to load support tickets. Please try again.</p>
        </div>
      )}

      {/* Tickets Table */}
      {!ticketsLoading && !ticketsError && (
        <>
          {filteredTickets.length > 0 ? (
            <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50 dark:bg-slate-800/50 border-b border-slate-200 dark:border-slate-700">
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Title</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase hidden md:table-cell">Employee</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Priority</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase hidden sm:table-cell">Created</th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {filteredTickets.map((ticket) => {
                    const employeeName = ticket.linked_employee 
                      ? `${ticket.linked_employee.first_name} ${ticket.linked_employee.last_name}`
                      : '-'
                    return (
                      <tr 
                        key={ticket.id} 
                        onClick={() => navigate(`/support/${ticket.id}`)}
                        className="hover:bg-slate-50 dark:hover:bg-slate-800/50 cursor-pointer group"
                      >
                        <td className="px-4 py-3">
                          <div>
                            <p className="text-sm font-medium text-slate-900 dark:text-white truncate max-w-xs">{ticket.title}</p>
                            <p className="text-xs text-slate-500 dark:text-slate-400 truncate max-w-xs mt-0.5">{ticket.description}</p>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-400 hidden md:table-cell">
                          {employeeName}
                        </td>
                        <td className="px-4 py-3">
                          <span className={cn('px-2 py-0.5 rounded text-xs font-medium uppercase', priorityStyles[ticket.priority] || priorityStyles.medium)}>
                            {ticket.priority}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <span className={cn('px-2 py-0.5 rounded-full text-xs font-medium', statusStyles[ticket.status as TicketStatus] || statusStyles.open)}>
                            {statusLabels[ticket.status] || ticket.status}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-500 dark:text-slate-400 hidden sm:table-cell">
                          {formatDate(ticket.created_at, { day: 'numeric', month: 'short' })}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1 justify-end">
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                                navigate(`/support/${ticket.id}`)
                              }}
                              className="p-1.5 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-lg text-slate-500"
                              title="View Ticket"
                            >
                              <Eye className="w-4 h-4" />
                            </button>
                            <button
                              onClick={(e) => handleDeleteClick(e, ticket)}
                              className="p-1.5 hover:bg-red-100 dark:hover:bg-red-900/20 rounded-lg text-red-500"
                              title="Delete Ticket"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 py-16 text-center">
              <Ticket className="w-12 h-12 mx-auto text-slate-300 dark:text-slate-700 mb-3" />
              <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-1">No tickets found</h3>
              <p className="text-slate-500 dark:text-slate-400">
                {search || statusFilter !== 'all' 
                  ? 'Try adjusting your filters' 
                  : 'Create your first support request'}
              </p>
            </div>
          )}
        </>
      )}

      {/* Create Request Modal */}
      <CreateRequestModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSuccess={() => {
          queryClient.invalidateQueries({ queryKey: ['support-tickets'] })
          queryClient.invalidateQueries({ queryKey: ['support-ticket-stats'] })
        }}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmModal
        isOpen={!!ticketToDelete}
        onClose={() => setTicketToDelete(null)}
        onConfirm={handleConfirmDelete}
        title="Delete Support Request"
        message={`Are you sure you want to delete "${ticketToDelete?.title}"? This action cannot be undone.`}
        confirmLabel="Delete"
        cancelLabel="Cancel"
        variant="danger"
        isLoading={deleteMutation.isPending}
      />
    </div>
  )
}
